import React, { FC, useMemo, useRef, useState } from 'react';
import { Button, message, Tooltip } from 'antd';
import { isInternalTeam, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { isInternalTech } from '../../utils/AuthUtils';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';

const LOCATION_CONTACT_TOOLTIP =
	"This user is a contact on the work order's location (facility in-charge or primary contact), " +
	'so they stay subscribed automatically and cannot be removed here. ' +
	"Update the location's contacts to change this.";

const RemoveSubscriberButton: FC<any> = ({
	currentUser,
	subscriber,
	onRemove,
	workOrder,
	loading,
	isContractorThread,
	isInternalWorkspace,
	allowInternalTechBuyerParity,
}): React.ReactElement => {
	const userType = useMemo(() => currentUser.userType, [currentUser.userType]);

	const [isRemoving, setIsRemoving] = useState(false);
	const removalInFlightRef = useRef(false);

	// Callers that already track removal state per subscriber pass `loading` in; the rest
	// just return their removal promise from `onRemove` and get the spinner from here.
	// The ref guard makes a double-click send only one request — the `loading`/`disabled`
	// props alone can't, since the re-render lands after the second click.
	const handleRemoveClick = (event) => {
		if (removalInFlightRef.current) return;
		const removal = onRemove(subscriber)(event);
		if (!removal || typeof removal.then !== 'function') return;
		removalInFlightRef.current = true;
		setIsRemoving(true);
		const clearRemovingState = () => {
			removalInFlightRef.current = false;
			setIsRemoving(false);
		};
		removal.then(clearRemovingState).catch(() => {
			clearRemovingState();
			message.error('Failed to remove subscriber. Please try again.');
		});
	};

	const isLoading = !!loading || isRemoving;

	// Members of the assigned supplier facility are protected from buyer removal —
	// unless that facility is the buyer's own internal service team (same company).
	const isBuyerAllowedToRemove = useMemo(
		() =>
			nullSafeGet('facilityId', subscriber) !== nullSafeGet('supplierFacilityId', workOrder) ||
			isInternalTeam(nullSafeGet('supplierFacility', workOrder)),
		[subscriber, workOrder]
	);

	const isSupplierAllowedToRemove = useMemo(
		() =>
			subscriber.contactType === ROLE_TYPES.SUPPLIER &&
			nullSafeGet('facilityId', subscriber) === nullSafeGet('facility.id', currentUser),
		[currentUser, subscriber]
	);

	// Internal techs are buyer-company staff, so they get buyer-level removal rights —
	// but only where a caller opts in via `allowInternalTechBuyerParity` (the supplier
	// work-order notes page): this shared button also serves walkthrough notes and the
	// internal workspace, which keep the same-facility supplier rule. Parity is further
	// limited to the root WO thread — contractor threads surface contractor-side contacts
	// in the buyer/supplier subscriber slots, where buyer-level eligibility would expose
	// cross-facility removals. `isContractorThread` lets callers flag contractor context
	// synchronously (e.g. from the URL) since `contractorWo` may be absent while the full
	// WO is still refetching.
	const isEffectivelyContractorThread =
		!!isContractorThread || !!nullSafeGet('contractorWo.id', workOrder);
	const isAllowedToRemove = useMemo(
		() =>
			userType === ROLE_TYPES.BUYER ||
			(!!allowInternalTechBuyerParity &&
				isInternalTech(currentUser) &&
				!isEffectivelyContractorThread)
				? isBuyerAllowedToRemove
				: isSupplierAllowedToRemove,
		[
			allowInternalTechBuyerParity,
			currentUser,
			isEffectivelyContractorThread,
			isBuyerAllowedToRemove,
			isSupplierAllowedToRemove,
			userType,
		]
	);

	// The backend folds the location's facility in-charge / primary contacts back into the buyer
	// subscriber list on every read, so a removal request for them succeeds but changes nothing.
	// `nonRemovableBuyerSubscriberEmails` is the server's list of exactly those emails. It is
	// buyer-subscriber specific, so it does not apply to the internal workspace thread.
	const isNonRemovableLocationContact = useMemo(() => {
		if (isInternalWorkspace) return false;
		const email = nullSafeGet('email', subscriber);
		if (!email) return false;
		const nonRemovableEmails = nullSafeGetOrElse(
			'nonRemovableBuyerSubscriberEmails',
			workOrder,
			[]
		);
		if (!Array.isArray(nonRemovableEmails)) return false;
		return nonRemovableEmails
			.map((nonRemovableEmail) => String(nonRemovableEmail).toLowerCase())
			.includes(String(email).toLowerCase());
	}, [isInternalWorkspace, subscriber, workOrder]);

	if (!isAllowedToRemove) return null;

	return isNonRemovableLocationContact ? (
		<Tooltip title={LOCATION_CONTACT_TOOLTIP}>
			{/* antd disabled buttons swallow hover events, so the tooltip needs a live wrapper */}
			<span style={{ cursor: 'not-allowed' }}>
				<Button disabled={true}>Remove</Button>
			</span>
		</Tooltip>
	) : (
		<OWButtonWithSupplierManagedLock
			loading={isLoading}
			disabled={isLoading}
			onClick={handleRemoveClick}
		>
			Remove
		</OWButtonWithSupplierManagedLock>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default connect(mapStateToProps)(RemoveSubscriberButton);
